<template>
  <div v-if="loadingMenu" class="collapse navbar-collapse sidenav-scroll-container">
    <SkeletonLoader type="sidenav" :items-count="6" />
  </div>
  <div v-else-if="menuError" class="collapse navbar-collapse sidenav-scroll-container">
    <div class="alert alert-warning mx-3 mt-3">
      <p>Error al cargar menú: {{ menuError }}</p>
      <button class="btn btn-sm btn-warning mt-2" @click="fetchMenu">
        Reintentar
      </button>
    </div>
  </div>
  <div v-else class="collapse navbar-collapse sidenav-scroll-container" id="sidenav-collapse-main">
    <ul class="navbar-nav">
      <!-- Inicio - siempre visible para usuarios autenticados, no depende de web_4connect_modules -->
      <li class="nav-item mt-4">
        <sidenav-collapse navText="Home" :to="{ name: 'Welcome' }">
          <template #icon>
            <div class="sidenav-icon-wrapper">
              <LazyIcon name="HomeIcon" class="sidenav-icon" :preload="true" />
            </div>
          </template>
        </sidenav-collapse>
      </li>

      <!-- Zonas y módulos dinámicos, obtenidos de /api/v2/me/menu -->
      <!-- (web_4connect_zones / web_4connect_modules, ya filtrados por activado y por permiso del usuario) -->
      <template v-for="zone in menuZones" :key="zone.id">
        <li class="mt-3 nav-item">
          <div class="section-header">
            <div class="section-icon">
              <i class="ri-settings-3-line"></i>
            </div>
            <h6
              class="section-title"
              :class="$store.state.isRTL ? 'me-4' : 'ms-2'"
            >
              {{ zone.nombre.toUpperCase() }}
            </h6>
          </div>
        </li>

        <li class="nav-item" v-for="module in zone.modules" :key="module.id">
          <sidenav-collapse :navText="module.nombre" :to="{ path: module.ruta }">
            <template #icon>
              <div class="sidenav-icon-wrapper">
                <i :class="module.icono || 'ri-list-check'" class="sidenav-icon"></i>
              </div>
            </template>
          </sidenav-collapse>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import axios from "axios";
import SidenavCollapse from "./SidenavCollapse.vue";
import SkeletonLoader from "@/components/UI/SkeletonLoader.vue";
import LazyIcon from "@/components/UI/LazyIcon.vue";
import { useDarkModeSafe } from "@/composables/useDarkModeSafe";

export default {
  name: "SidenavList",
  props: {
    cardBg: String,
  },
  data() {
    return {
      title: "4CONNECT",
      controls: "dashboardsExamples",
      isActive: "active",
      menuZones: [],
      loadingMenu: true,
      menuError: null,
      fetchAttempts: 0,
      maxFetchAttempts: 3
    };
  },
  components: {
    SidenavCollapse,
    SkeletonLoader,
    LazyIcon
  },
  setup() {
    const { isDark } = useDarkModeSafe();
    return {
      isDark
    };
  },
  computed: {
    // Verificar si el usuario está logueado
    isUserLoggedIn() {
      return this.$store.getters['auth/loggedIn'];
    },

    // Clase dinámica para el tema
    themeClass() {
      return this.isDark ? 'dark-theme' : 'light-theme';
    }
  },
  watch: {
    // Observar cambios en el estado de login
    isUserLoggedIn: {
      handler(isLoggedIn) {
        if (isLoggedIn) {
          console.log('Usuario logueado, obteniendo menú');
          this.fetchMenuWithDelay();
        } else {
          console.log('Usuario no logueado, limpiando menú');
          this.menuZones = [];
          this.loadingMenu = false;
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.isUserLoggedIn) {
      this.fetchMenuWithDelay();
    } else {
      this.loadingMenu = false;
    }
  },
  methods: {
    getRoute() {
      const routeArr = this.$route.path.split("/");
      return routeArr[1];
    },

    // Pequeño delay para que el token se guarde si acabamos de hacer login
    async fetchMenuWithDelay(delay = 300) {
      if (delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      this.fetchMenu();
    },

    // Obtiene el árbol zonas -> módulos ya filtrado por el backend
    // (activado = 'SI' y permiso del usuario vía web_4connect_module_permissions.id_mod)
    async fetchMenu() {
      if (!this.isUserLoggedIn) {
        console.log('Usuario no logueado, no obteniendo menú');
        this.loadingMenu = false;
        return;
      }

      if (this.fetchAttempts >= this.maxFetchAttempts) {
        console.warn('Máximo número de intentos alcanzado para obtener el menú');
        this.menuError = "Error al conectar con el servidor";
        this.loadingMenu = false;
        return;
      }

      try {
        this.loadingMenu = true;
        this.menuError = null;
        this.fetchAttempts++;

        const tokenJWT = localStorage.getItem('auth_token');

        if (!tokenJWT) {
          console.log("No se encontró token JWT en localStorage");

          if (this.fetchAttempts === 1) {
            this.loadingMenu = false;
            console.log("Esperando token, reintentando en 1 segundo...");
            setTimeout(() => this.fetchMenu(), 1000);
            return;
          }

          this.menuError = "No hay sesión activa";
          this.menuZones = [];
          this.loadingMenu = false;

          this.$store.commit('auth/logMeOut');

          return;
        }

        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';

        const menuResponse = await axios.get(`${apiUrl}/me/menu`, {
          headers: {
            'Authorization': `Bearer ${tokenJWT}`,
            'Accept': 'application/json'
          }
        });

        if (menuResponse.data && menuResponse.data.success) {
          this.menuZones = menuResponse.data.data || [];
          this.fetchAttempts = 0;
        } else {
          console.warn("La respuesta de /me/menu no contiene datos válidos");
          this.menuError = "Formato de respuesta incorrecto";
          this.menuZones = [];
        }
      } catch (error) {
        console.error("Error al obtener el menú:", error);
        this.menuError = "Error de conexión";
        this.menuZones = [];

        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          console.warn("Error de autenticación, limpiando sesión");
          this.menuError = "Sesión expirada";

          localStorage.removeItem('auth_token');
          localStorage.removeItem('userF');
          this.$store.commit('auth/logMeOut');

          this.fetchAttempts = 0;
        }
      } finally {
        this.loadingMenu = false;
      }
    }
  },
};
</script>

<style scoped>
/* Variables CSS para modo claro y oscuro */
:root {
  /* Modo claro */
  --liquid-glass-bg: rgba(255, 255, 255, 0.15);
  --liquid-glass-bg-hover: rgba(255, 255, 255, 0.25);
  --liquid-glass-border: rgba(255, 255, 255, 0.3);
  --liquid-glass-border-hover: rgba(255, 255, 255, 0.5);
  --liquid-glass-blur: 20px;
  --liquid-glass-blur-light: 12px;
  --liquid-glass-saturate: 180%;
  --liquid-glass-brightness: 1.1;
  
  --dm-accent-primary: #17ad37;
  --dm-accent-secondary: #98ec2d;
  --dm-bg-primary: #ffffff;
  --dm-text-secondary: #6b7280;
  --dm-border-primary: rgba(203, 213, 225, 0.3);
  --dm-shadow-accent: rgba(23, 173, 55, 0.3);
  --dm-shadow-accent-hover: rgba(23, 173, 55, 0.4);
  
  --liquid-shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --liquid-shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  --liquid-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  --liquid-transition-smooth: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Variables para modo oscuro */
body.dark-mode-active {
  --liquid-glass-bg: rgba(30, 41, 59, 0.4);
  --liquid-glass-bg-hover: rgba(30, 41, 59, 0.6);
  --liquid-glass-border: rgba(71, 85, 105, 0.4);
  --liquid-glass-border-hover: rgba(71, 85, 105, 0.6);
  --liquid-glass-brightness: 0.9;
  
  --dm-bg-primary: #1e293b;
  --dm-text-secondary: #94a3b8;
  --dm-border-primary: rgba(71, 85, 105, 0.3);
  --dm-shadow-accent: rgba(23, 173, 55, 0.4);
  --dm-shadow-accent-hover: rgba(23, 173, 55, 0.5);
}

/* Contenedor principal del sidebar con liquid glass */
.navbar-nav {
  padding: 0.5rem 0 1.5rem 0;
  gap: 0.125rem;
  display: flex !important;
  flex-direction: column !important;
  flex: 1 !important;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 0;
  background: var(--liquid-glass-bg) !important;
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  border-radius: 0 0 1rem 0;
  position: relative;
  box-shadow: var(--liquid-shadow-sm) !important;
  border-right: 1px solid var(--liquid-glass-border) !important;
  /* Optimizaciones para scroll suave */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* Wrapper de iconos mejorado */
.sidenav-icon-wrapper {
  width: 1.5rem;
  height: 1.5rem;
  display: inline-block;
  position: relative;
}

.sidenav-icon-wrapper svg {
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
}

/* Estilos para las cabeceras de sección con liquid glass */
.section-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  margin: 0.5rem 0.5rem 0.25rem;
  border-radius: 0.75rem;
  background: var(--liquid-glass-bg) !important;
  border: 1px solid var(--liquid-glass-border) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  position: relative;
  overflow: hidden;
  transition: var(--liquid-transition-smooth);
  box-shadow: var(--liquid-shadow-xs) !important;
}

.section-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.6) 20%, 
    rgba(255, 255, 255, 0.8) 50%, 
    rgba(255, 255, 255, 0.6) 80%, 
    transparent
  );
  z-index: 1;
}

.section-header:hover {
  background: var(--liquid-glass-bg-hover) !important;
  border-color: var(--liquid-glass-border-hover) !important;
  box-shadow: var(--liquid-shadow-sm) !important;
  transform: translateY(-1px) scale(1.005);
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.15) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.15) !important;
}

.section-icon {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.375rem;
  background: linear-gradient(135deg, var(--dm-accent-primary, #17ad37) 0%, var(--dm-accent-secondary, #98ec2d) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.625rem;
  box-shadow: 0 4px 12px var(--dm-shadow-accent, rgba(23, 173, 55, 0.3));
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.section-icon i {
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
}

.section-header:hover .section-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 6px 16px var(--dm-shadow-accent-hover, rgba(23, 173, 55, 0.4));
}

.section-title {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--dm-text-secondary, #6b7280);
  margin: 0;
  position: relative;
  z-index: 1;
  transition: color 0.3s ease;
}

.section-header:hover .section-title {
  color: var(--dm-accent-primary, #17ad37);
}

/* Estados de carga mejorados */
.spinner-border {
  width: 2.5rem;
  height: 2.5rem;
  color: var(--dm-accent-primary, #17ad37);
  border-width: 0.25rem;
  animation: spin 1s linear infinite, pulse-opacity 2s ease-in-out infinite;
  box-shadow: 0 0 0 2px var(--dm-bg-primary, #ffffff), 
              0 0 0 3px var(--dm-accent-primary, #17ad37);
  border-radius: 50%;
  transition: color 0.3s ease, box-shadow 0.3s ease;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse-opacity {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* Alert mejorado */
.alert-warning {
  background: var(--dm-bg-warning, linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%));
  border: 1px solid var(--dm-border-warning, rgba(251, 191, 36, 0.2));
  color: var(--dm-text-warning, #92400e);
  border-radius: 0.75rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px var(--dm-shadow-warning, rgba(251, 191, 36, 0.1));
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}

.alert-warning .btn {
  background: linear-gradient(135deg, var(--dm-btn-warning, #fbbf24) 0%, var(--dm-btn-warning-hover, #f59e0b) 100%);
  border: none;
  color: var(--dm-btn-warning-text, white);
  font-weight: 600;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px var(--dm-shadow-warning-btn, rgba(251, 191, 36, 0.3));
}

.alert-warning .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--dm-shadow-warning-btn-hover, rgba(251, 191, 36, 0.4));
}

/* Contenedor principal con liquid glass */
.navbar-collapse,
.sidenav-scroll-container {
  /* background: var(--liquid-glass-bg) !important; */
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  /* border: 1px solid var(--liquid-glass-border) !important; */
  /* border-radius: 1rem; */
  flex: 1 !important;
  min-height: 0 !important;
  height: auto !important;
  max-height: none !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  /* box-shadow: var(--liquid-shadow-md) !important; */
  position: relative;
}

/* Efectos de scroll mejorado y suave */
.navbar-nav {
  scrollbar-width: thin;
  scrollbar-color: rgba(23, 173, 55, 0.4) transparent;
}

.navbar-nav::-webkit-scrollbar {
  width: 6px;
}

.navbar-nav::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin: 0.5rem 0;
}

.navbar-nav::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, 
    rgba(23, 173, 55, 0.6), 
    rgba(152, 236, 45, 0.6)
  );
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.navbar-nav::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to bottom, 
    rgba(23, 173, 55, 0.8), 
    rgba(152, 236, 45, 0.8)
  );
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.navbar-nav::-webkit-scrollbar-thumb:active {
  background: linear-gradient(to bottom, 
    rgba(23, 173, 55, 1), 
    rgba(152, 236, 45, 1)
  );
}

/* Scroll fade effects - solo arriba para no interferir con el contenido */
.navbar-nav::before {
  content: '';
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 15px;
  background: linear-gradient(to bottom, var(--liquid-glass-bg), transparent);
  pointer-events: none;
  z-index: 10;
  margin-bottom: -15px;
}

/* Animaciones de entrada para los elementos del menú */
.nav-item {
  animation: slideInFromLeft 0.3s ease-out;
  animation-fill-mode: both;
}

.nav-item:nth-child(1) { animation-delay: 0.1s; }
.nav-item:nth-child(2) { animation-delay: 0.15s; }
.nav-item:nth-child(3) { animation-delay: 0.2s; }
.nav-item:nth-child(4) { animation-delay: 0.25s; }
.nav-item:nth-child(5) { animation-delay: 0.3s; }
.nav-item:nth-child(6) { animation-delay: 0.35s; }
.nav-item:nth-child(7) { animation-delay: 0.4s; }
.nav-item:nth-child(8) { animation-delay: 0.45s; }
.nav-item:nth-child(9) { animation-delay: 0.5s; }
.nav-item:nth-child(n+10) { animation-delay: 0.55s; }

@keyframes slideInFromLeft {
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Responsive design */
@media (max-width: 1200px) {
  .section-header {
    padding: 0.5rem 0.75rem;
    margin: 0.375rem 0.375rem 0.125rem;
  }
  
  .section-icon {
    width: 1.375rem;
    height: 1.375rem;
    margin-right: 0.5625rem;
  }
  
  .section-icon i {
    font-size: 0.6875rem;
  }
  
  .section-title {
    font-size: 0.625rem;
  }
}

@media (max-width: 992px) {
  .section-header {
    padding: 0.4375rem 0.6875rem;
    margin: 0.3125rem 0.3125rem 0.0625rem;
  }
  
  .section-icon {
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.5rem;
  }
  
  .section-icon i {
    font-size: 0.625rem;
  }
  
  .section-title {
    font-size: 0.5625rem;
  }
}

/* Efecto liquid glass adicional para el fondo */
.navbar-collapse::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.4) 20%, 
    rgba(255, 255, 255, 0.6) 50%, 
    rgba(255, 255, 255, 0.4) 80%, 
    transparent
  );
  border-radius: 1rem 1rem 0 0;
  z-index: 1;
}

/* Estados específicos para modo oscuro */
body.dark-mode-active .navbar-collapse,
body.dark-mode-active .sidenav-scroll-container {
  background: var(--liquid-glass-bg) !important;
  border-color: var(--liquid-glass-border) !important;
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  flex: 1 !important;
  min-height: 0 !important;
  height: auto !important;
  max-height: none !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

body.dark-mode-active .navbar-nav {
  background: var(--liquid-glass-bg) !important;
  border-right-color: var(--liquid-glass-border) !important;
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  padding: 0.5rem 0 1.5rem 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  flex: 1 !important;
  min-height: 0 !important;
}

body.dark-mode-active .navbar-nav::-webkit-scrollbar-track {
  background: rgba(71, 85, 105, 0.3);
}

body.dark-mode-active .navbar-nav::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, 
    rgba(23, 173, 55, 0.8), 
    rgba(152, 236, 45, 0.8)
  );
  border-color: rgba(100, 116, 139, 0.4);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

body.dark-mode-active .navbar-nav::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to bottom, 
    rgba(23, 173, 55, 1), 
    rgba(152, 236, 45, 1)
  );
  border-color: rgba(100, 116, 139, 0.6);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
}

body.dark-mode-active .section-header {
  background: var(--liquid-glass-bg) !important;
  border: 1px solid var(--liquid-glass-border) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
}

body.dark-mode-active .section-header:hover {
  background: var(--liquid-glass-bg-hover) !important;
  border-color: var(--liquid-glass-border-hover) !important;
}

body.dark-mode-active .section-title {
  color: rgba(148, 163, 184, 0.9) !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

body.dark-mode-active .section-header:hover .section-title {
  color: rgba(152, 236, 45, 0.9) !important;
}

body.dark-mode-active .section-icon {
  box-shadow: 0 4px 12px rgba(23, 173, 55, 0.5);
}

body.dark-mode-active .section-header:hover .section-icon {
  box-shadow: 0 6px 16px rgba(23, 173, 55, 0.6);
}

body.dark-mode-active .spinner-border {
  border-color: var(--dm-accent-primary) transparent var(--dm-accent-primary) transparent;
  box-shadow: 0 0 0 2px rgba(30, 41, 59, 1), 
              0 0 0 3px var(--dm-accent-primary);
}

body.dark-mode-active .alert-warning {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%);
  border-color: rgba(251, 191, 36, 0.3);
  color: #fbbf24;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.2);
}

body.dark-mode-active .nav-subitem .sidenav-icon-wrapper.sidenav-icon-sub {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.25) 0%, rgba(152, 236, 45, 0.25) 100%);
  border-color: rgba(23, 173, 55, 0.5);
  box-shadow: 0 2px 8px rgba(23, 173, 55, 0.3);
}

body.dark-mode-active .nav-subitem .sidenav-icon-wrapper.sidenav-icon-sub i {
  color: rgba(152, 236, 45, 0.9) !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  font-weight: 600;
}

body.dark-mode-active .nav-subitem:hover .sidenav-icon-wrapper.sidenav-icon-sub {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.4) 0%, rgba(152, 236, 45, 0.4) 100%);
  border-color: rgba(152, 236, 45, 0.7);
  box-shadow: 0 4px 12px rgba(23, 173, 55, 0.4);
}

body.dark-mode-active .nav-subitem:hover .sidenav-icon-wrapper.sidenav-icon-sub i {
  color: rgba(152, 236, 45, 1) !important;
  transform: scale(1.1);
}

/* Efectos fade para modo oscuro - solo arriba */
body.dark-mode-active .navbar-nav::before {
  background: linear-gradient(to bottom, var(--liquid-glass-bg), transparent);
}

/* Mejoras para modo claro - asegurar visibilidad */
body:not(.dark-mode-active) .navbar-collapse,
body:not(.dark-mode-active) .sidenav-scroll-container {
  background: rgba(255, 255, 255, 0.4) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
}

body:not(.dark-mode-active) .navbar-nav {
  background: rgba(255, 255, 255, 0.3) !important;
  border-right: 1px solid rgba(255, 255, 255, 0.5) !important;
}

body:not(.dark-mode-active) .section-header {
  background: rgba(255, 255, 255, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
  box-shadow: 
    0 1px 0 0 rgba(255, 255, 255, 0.6) inset,
    0 0 0 1px rgba(255, 255, 255, 0.25) inset,
    0 4px 8px rgba(0, 0, 0, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

body:not(.dark-mode-active) .section-header:hover {
  background: rgba(255, 255, 255, 0.45) !important;
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  box-shadow: 
    0 1px 0 0 rgba(255, 255, 255, 0.6) inset,
    0 0 0 1px rgba(255, 255, 255, 0.25) inset,
    0 8px 16px rgba(0, 0, 0, 0.18),
    0 4px 8px rgba(0, 0, 0, 0.12) !important;
}

/* Asegurar visibilidad del texto en modo claro */
body:not(.dark-mode-active) .section-title {
  color: rgba(55, 65, 81, 0.8) !important;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

body:not(.dark-mode-active) .section-header:hover .section-title {
  color: rgba(23, 173, 55, 0.9) !important;
}

@media (max-width: 768px) {
  .section-header {
    padding: 0.375rem 0.625rem;
    margin: 0.25rem 0.25rem 0.0625rem;
  }
  
  .section-icon {
    width: 1.125rem;
    height: 1.125rem;
    margin-right: 0.4375rem;
  }
  
  .section-icon i {
    font-size: 0.5625rem;
  }
  
  .section-title {
    font-size: 0.5rem;
  }
}

/* Optimizaciones adicionales para scroll responsive */
@media (max-height: 800px) {
  .navbar-nav {
    padding: 0.25rem 0 1rem 0 !important;
    gap: 0.0625rem !important;
  }

  .section-header {
    padding: 0.375rem 0.75rem;
    margin: 0.25rem 0.5rem 0.125rem;
  }

  body.dark-mode-active .navbar-nav {
    padding: 0.25rem 0 1rem 0 !important;
  }
}

@media (max-height: 650px) {
  .navbar-nav {
    padding: 0.125rem 0 0.75rem 0 !important;
    gap: 0 !important;
  }

  .nav-item {
    margin: 0;
  }

  .section-header {
    padding: 0.25rem 0.625rem;
    margin: 0.125rem 0.375rem 0.0625rem;
  }

  body.dark-mode-active .navbar-nav {
    padding: 0.125rem 0 0.75rem 0 !important;
  }
}

/* Estilos para subcategorías */
.nav-subitem {
  margin-left: 1rem;
  margin-top: 0.125rem;
  margin-bottom: 0.125rem;
  opacity: 0.9;
  position: relative;
}

.nav-subitem::before {
  content: '';
  position: absolute;
  left: -0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 20px;
  background: linear-gradient(to bottom, transparent, var(--dm-accent-primary, #17ad37), transparent);
  border-radius: 1px;
}

.nav-subitem .sidenav-icon-wrapper.sidenav-icon-sub {
  width: 1.5rem;
  height: 1.5rem;
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.15) 0%, rgba(152, 236, 45, 0.15) 100%);
  border: 1px solid rgba(23, 173, 55, 0.3);
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 1px 4px rgba(23, 173, 55, 0.2);
}

.nav-subitem .sidenav-icon-wrapper.sidenav-icon-sub i {
  font-size: 0.875rem;
  color: #17ad37;
  font-weight: 500;
  transition: all 0.3s ease;
}

.nav-subitem:hover .sidenav-icon-wrapper.sidenav-icon-sub {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.25) 0%, rgba(152, 236, 45, 0.25) 100%);
  border-color: rgba(23, 173, 55, 0.5);
  box-shadow: 0 2px 8px rgba(23, 173, 55, 0.3);
  transform: scale(1.05);
}

.nav-subitem:hover .sidenav-icon-wrapper.sidenav-icon-sub i {
  color: #098425;
  transform: scale(1.1);
}

.nav-subitem:hover::before {
  background: linear-gradient(to bottom, var(--dm-accent-primary, #17ad37), var(--dm-accent-secondary, #98ec2d), var(--dm-accent-primary, #17ad37));
  width: 3px;
  height: 25px;
}

/* Estados específicos para modo oscuro en subcategorías */
body.dark-mode-active .nav-subitem .sidenav-icon-wrapper.sidenav-icon-sub {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.15) 0%, rgba(152, 236, 45, 0.15) 100%);
  border-color: rgba(23, 173, 55, 0.3);
}

/* Responsive para subcategorías */
@media (max-width: 1200px) {
  .nav-subitem {
    margin-left: 0.75rem;
  }
  
  .nav-subitem::before {
    left: -0.375rem;
  }
}

@media (max-width: 992px) {
  .nav-subitem {
    margin-left: 0.5rem;
  }
  
  .nav-subitem::before {
    left: -0.25rem;
    width: 1.5px;
    height: 15px;
  }
  
  .nav-subitem .sidenav-icon-wrapper.sidenav-icon-sub {
    width: 1.375rem;
    height: 1.375rem;
  }
  
  .nav-subitem .sidenav-icon-wrapper.sidenav-icon-sub i {
    font-size: 0.75rem;
  }
}
</style>